import {
  Component,
  input,
  signal,
  inject,
  ViewChild,
  OnInit,
  OnChanges,
  SimpleChanges,
  output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { FieldsetModule } from 'primeng/fieldset';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import {
  MAX_VALOR_TRANSPORTE_ADICIONAL,
  NO_VALUE,
  YES_VALUE
} from 'src/app/common/constants-ejb';
import { TopeValorDirective } from 'src/app/common/directives/tope-valor.directive';
import { sanitizeTextarea } from 'src/app/common/input-utils';

import { BuscadorMunicipioComponent } from '../../../shared/buscadores/buscador-municipio.component';
import { BuscadorPoblacionComponent } from '../../../shared/buscadores/buscador-poblacion.component';
import { Municipio } from 'src/app/domain/model/municipio/municipio';
import { Poblacion } from 'src/app/domain/model/transporte/poblacion';

interface TransporteSignals {
  dsmunicipioOrigen: { set: (value: string) => void };
  cdsuraOrigen: { set: (value: string) => void };
  dsmunicipioEntrega: { set: (value: string) => void };
  cdsuraEntrega: { set: (value: string) => void };
  dspoblacion: { set: (value: string) => void };
  mostrarTransporteAdicional: { set: (value: boolean) => void };
  requiereObservacionesAdicional: { set: (value: boolean) => void };
}

/**
 * Interface para municipio precargado
 */
export interface MunicipioPrecargado {
  cdmunicipio?: string; // Código DANE para FK
  cdsura?: string; // Código SURA para tarifas
  dsmunicipio?: string;
}

/**
 * DUMB COMPONENT: SeccionTransporte
 *
 * Sección de transporte para cumplimientos de tipo CAMPO.
 * REUTILIZA los componentes BuscadorMunicipioComponent y BuscadorPoblacionComponent
 *
 * Campos del legacy (CreateOrUpdateCumplimientoViewImpl.java):
 * - Municipio de Origen * (lupa → TPSC_MUNICIPIOS)
 * - Municipio de Entrega * (lupa → TPSC_MUNICIPIOS)
 * - Población (lupa → W094_SGS_TARIFAS_TRANSPORTE)
 * - Pago Transporte a Empleado (Si/No)
 * - Valor Transporte (readonly, calculado desde tarifa)
 * - Pago Transporte Adicional (Si/No)
 * - Valor Transporte Adicional
 * - Observaciones Transporte Adicional
 *
 * @example
 * <app-seccion-transporte
 *   [formGroup]="formCumplimiento"
 *   [municipioOrigenInicial]="{ cdmunicipio: '05001', cdsura: '657', dsmunicipio: 'MEDELLÍN' }"
 *   [municipioEntregaInicial]="{ cdmunicipio: '05615', cdsura: '1234', dsmunicipio: 'RIONEGRO' }" />
 */
@Component({
  selector: 'app-seccion-transporte',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FieldsetModule,
    RadioButtonModule,
    InputNumberModule,
    InputTextModule,
    InputTextarea,
    ButtonModule,
    TooltipModule,
    BuscadorMunicipioComponent,
    BuscadorPoblacionComponent,
    TopeValorDirective
  ],
  template: `
    <p-fieldset
      legend="Información de Transporte"
      [toggleable]="true"
      styleClass="mb-4"
    >
      <div
        class="grid grid-cols-1 md:grid-cols-3 gap-4"
        [formGroup]="formGroup()"
      >
        <!-- Municipio Origen -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Municipio de Origen <span class="text-red-500">*</span>
          </label>
          <div class="flex gap-2">
            <input
              pInputText
              [value]="dsmunicipioOrigen()"
              placeholder="Seleccione municipio de origen"
              readonly
              class="flex-1 cursor-pointer"
              (click)="buscadorMunicipioOrigen.abrir()"
            />
            <button
              pButton
              icon="pi pi-search"
              class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
              pTooltip="Buscar municipio"
              (click)="buscadorMunicipioOrigen.abrir()"
            ></button>
          </div>
        </div>

        <!-- Municipio Entrega -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Municipio de Entrega <span class="text-red-500">*</span>
          </label>
          <div class="flex gap-2">
            <input
              pInputText
              [value]="dsmunicipioEntrega()"
              placeholder="Seleccione municipio de entrega"
              readonly
              class="flex-1 cursor-pointer"
              (click)="buscadorMunicipioEntrega.abrir()"
            />
            <button
              pButton
              icon="pi pi-search"
              class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
              pTooltip="Buscar municipio"
              (click)="buscadorMunicipioEntrega.abrir()"
            ></button>
          </div>
        </div>

        <!-- Población -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Población</label
          >
          <div class="flex gap-2">
            <input
              pInputText
              [value]="dspoblacion()"
              placeholder="Seleccione población"
              readonly
              class="flex-1 cursor-pointer"
              [class.opacity-50]="!cdsuraOrigen() || !cdsuraEntrega()"
              (click)="abrirBuscadorPoblacion()"
            />
            <button
              pButton
              icon="pi pi-search"
              class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
              pTooltip="Buscar población"
              [disabled]="!cdsuraOrigen() || !cdsuraEntrega()"
              (click)="abrirBuscadorPoblacion()"
            ></button>
          </div>
          @if (!cdsuraOrigen() || !cdsuraEntrega()) {
            <small class="text-gray-500"
              >Seleccione primero los municipios</small
            >
          }
        </div>

        <!-- Pago Transporte Empleado -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >Pago Transporte a Empleado</label
          >
          <div class="flex gap-4">
            <div class="flex items-center">
              <p-radioButton
                name="snpagoTransporteEmpleado"
                value="S"
                formControlName="snpagoTransporteEmpleado"
                inputId="pagoS"
                (onClick)="onPagoTransporteEmpleadoChange()"
              >
              </p-radioButton>
              <label for="pagoS" class="ml-2 text-sm cursor-pointer">Si</label>
            </div>
            <div class="flex items-center">
              <p-radioButton
                name="snpagoTransporteEmpleado"
                value="N"
                formControlName="snpagoTransporteEmpleado"
                inputId="pagoN"
                (onClick)="onPagoTransporteEmpleadoChange()"
              >
              </p-radioButton>
              <label for="pagoN" class="ml-2 text-sm cursor-pointer">No</label>
            </div>
          </div>
        </div>

        <!-- Valor Transporte (readonly, calculado desde tarifa) -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Valor Transporte</label
          >
          <p-inputNumber
            formControlName="ptvalorTransporte"
            mode="currency"
            currency="COP"
            locale="es-CO"
            [readonly]="true"
            [inputStyle]="{ 'background-color': '#f3f4f6' }"
            [style]="{ width: '100%' }"
          >
          </p-inputNumber>
        </div>

        <div></div>
        <!-- Spacer para alinear grid -->

        <!-- Pago Transporte Adicional -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >Pago Transporte Adicional</label
          >
          <div class="flex gap-4">
            <div class="flex items-center">
              <p-radioButton
                name="snrequiereTransporteAdicional"
                value="S"
                formControlName="snrequiereTransporteAdicional"
                inputId="adicionalS"
              >
              </p-radioButton>
              <label for="adicionalS" class="ml-2 text-sm cursor-pointer"
                >Si</label
              >
            </div>
            <div class="flex items-center">
              <p-radioButton
                name="snrequiereTransporteAdicional"
                value="N"
                formControlName="snrequiereTransporteAdicional"
                inputId="adicionalN"
              >
              </p-radioButton>
              <label for="adicionalN" class="ml-2 text-sm cursor-pointer"
                >No</label
              >
            </div>
          </div>
        </div>

        <!-- Valor Transporte Adicional (solo visible cuando Pago Transporte Adicional = Si) -->
        @if (mostrarTransporteAdicional()) {
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Valor Transporte Adicional</label
            >
            <!-- Legacy: tbValorTransporteAdicional — solo dígitos, tope MAX_TTE_ADIC_CMPL -->
            <p-inputNumber
              formControlName="ptvalorTransporteAdicional"
              mode="currency"
              currency="COP"
              locale="es-CO"
              [min]="0"
              [appTopeValor]="maxTransporteAdicional()"
              [minFractionDigits]="0"
              [maxFractionDigits]="0"
              [style]="{ width: '100%' }"
            >
            </p-inputNumber>
          </div>

          <!-- Observaciones Transporte Adicional -->
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Observaciones Transporte Adicional
              @if (requiereObservacionesAdicional()) {
                <span class="text-red-500">*</span>
              }
            </label>
            <textarea
              pInputTextarea
              formControlName="dsobservacionesTransporteAdicional"
              (input)="sanitizeObservacionesTransporteAdicional()"
              rows="2"
              class="w-full"
              pTooltip="Ingrese las observaciones del transporte adicional"
            ></textarea>
          </div>
        }
      </div>
    </p-fieldset>

    <!-- Buscadores de Municipio (diálogos modales reutilizables) -->
    <app-buscador-municipio
      #buscadorMunicipioOrigen
      (municipioSeleccionado)="onMunicipioOrigenSeleccionado($event)"
    />

    <app-buscador-municipio
      #buscadorMunicipioEntrega
      (municipioSeleccionado)="onMunicipioEntregaSeleccionado($event)"
    />

    <app-buscador-poblacion
      #buscadorPoblacionRef
      [cdsuraOrigen]="cdsuraOrigen()"
      [cdsuraDestino]="cdsuraEntrega()"
      (poblacionSeleccionada)="onPoblacionSeleccionada($event)"
    />
  `
})
export class SeccionTransporteComponent implements OnInit, OnChanges {
  private readonly messageService = inject(MessageService);

  @ViewChild('buscadorMunicipioOrigen')
  buscadorMunicipioOrigen!: BuscadorMunicipioComponent;
  @ViewChild('buscadorMunicipioEntrega')
  buscadorMunicipioEntrega!: BuscadorMunicipioComponent;
  @ViewChild('buscadorPoblacionRef')
  buscadorPoblacionRef!: BuscadorPoblacionComponent;

  formGroup = input.required<FormGroup>();
  municipioOrigenInicial = input<MunicipioPrecargado | null>(null);
  municipioEntregaInicial = input<MunicipioPrecargado | null>(null);
  nmconsecutivoOficina = input<number | undefined>(undefined);
  nmconsecutivoCliente = input<number | undefined>(undefined);
  nmconsecutivoAsesor = input<number | undefined>(undefined);
  /** Tope del valor de transporte adicional (legacy MAX_TTE_ADIC_CMPL). */
  maxTransporteAdicional = input<number>(MAX_VALOR_TRANSPORTE_ADICIONAL);
  /** Callback para obtener tarifa (evita inyectar PoblacionRepository en dumb component) */
  onObtenerTarifa =
    input<
      (params: {
        cdsuraOrigen: string;
        cdsuraDestino: string;
        cdpoblacion?: string;
        nmconsecutivoOficina?: number;
        nmconsecutivoCliente?: number;
        nmconsecutivoAsesor?: number;
      }) => Promise<number | null>
    >();

  dsmunicipioOrigen = signal('');
  cdsuraOrigen = signal('');
  dsmunicipioEntrega = signal('');
  cdsuraEntrega = signal('');
  dspoblacion = signal('');
  mostrarTransporteAdicional = signal<boolean>(false);
  requiereObservacionesAdicional = signal<boolean>(false);

  private municipiosInicializados = false;

  /**
   * Contador de generación para descartar respuestas de tarifa obsoletas.
   *
   * Cuando el usuario cambia origen y entrega en sucesión rápida se disparan
   * dos llamadas a `cargarTarifaTransporte` sin cancelación (fire-and-forget).
   * `await` solo ordena el código dentro de UNA invocación; no serializa dos
   * invocaciones distintas de la misma función async — la que responda
   * primero pinta el valor, y la otra, al resolver después, lo sobrescribe
   * sin importar cuál se disparó primero. Como la ruta de respaldo por
   * oficina hace consultas adicionales, suele ser la más lenta: si gana la
   * carrera, sobrescribe un valor correcto por municipios con el de oficina.
   *
   * Cada invocación toma su propio número de generación al arrancar y solo
   * escribe en el formulario si sigue siendo la más reciente al resolver.
   */
  private tarifaRequestId = 0;

  private get signals(): TransporteSignals {
    return {
      dsmunicipioOrigen: this.dsmunicipioOrigen,
      cdsuraOrigen: this.cdsuraOrigen,
      dsmunicipioEntrega: this.dsmunicipioEntrega,
      cdsuraEntrega: this.cdsuraEntrega,
      dspoblacion: this.dspoblacion,
      mostrarTransporteAdicional: this.mostrarTransporteAdicional,
      requiereObservacionesAdicional: this.requiereObservacionesAdicional
    };
  }

  ngOnInit(): void {
    this.initSubscriptions(this.formGroup(), this.signals);
    this.loadInitialMunicipios();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['municipioOrigenInicial'] ||
        changes['municipioEntregaInicial']) &&
      !this.municipiosInicializados
    ) {
      this.loadInitialMunicipios();
    }
  }

  private loadInitialMunicipios(): void {
    const loaded = this.cargarMunicipiosIniciales(
      this.municipioOrigenInicial(),
      this.municipioEntregaInicial(),
      this.formGroup(),
      this.signals
    );
    if (loaded) this.municipiosInicializados = true;
  }

  resetMunicipios(): void {
    // Invalida cualquier consulta de tarifa en curso: si resolviera después
    // de este reset, el guard de cargarTarifaTransporte la descartará.
    this.tarifaRequestId++;
    this.resetSignals(this.signals);
    this.formGroup().patchValue({ cdpoblacion: '', ptvalorTransporte: null });
    this.municipiosInicializados = false;
  }

  onMunicipioOrigenSeleccionado(municipio: Municipio): void {
    this.seleccionarMunicipioOrigen(municipio, this.formGroup(), this.signals);
    this.messageService.add({
      severity: 'success',
      summary: 'Municipio origen seleccionado',
      detail: municipio.dsmunicipio || '',
      life: 2000
    });
  }

  onMunicipioEntregaSeleccionado(municipio: Municipio): void {
    this.seleccionarMunicipioEntrega(municipio, this.formGroup(), this.signals);
    this.messageService.add({
      severity: 'success',
      summary: 'Municipio entrega seleccionado',
      detail: municipio.dsmunicipio || '',
      life: 2000
    });
  }

  abrirBuscadorPoblacion(): void {
    if (!this.cdsuraOrigen() || !this.cdsuraEntrega()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atención',
        detail: 'Debe seleccionar primero los municipios de origen y entrega',
        life: 3000
      });
      return;
    }
    this.buscadorPoblacionRef.abrir();
  }

  onPoblacionSeleccionada(poblacion: Poblacion): void {
    this.seleccionarPoblacion(poblacion, this.formGroup(), this.signals);
    this.recalcularTarifaSiAplica(this.formGroup());
    this.messageService.add({
      severity: 'success',
      summary: 'Población seleccionada',
      detail: poblacion.dspoblacion || '',
      life: 2000
    });
  }

  onPagoTransporteEmpleadoChange(): void {
    const form = this.formGroup();
    const pagoEmpleado = form.get('snpagoTransporteEmpleado')?.value;
    if (pagoEmpleado === YES_VALUE) {
      if (this.cdsuraOrigen() && this.cdsuraEntrega()) {
        this.messageService.add({
          severity: 'info',
          summary: 'Recordatorio',
          detail:
            'Recuerda ingresar los municipios reales de prestación del servicio conforme a la ruta realizada',
          life: 5000
        });
        this.cargarTarifaTransporte(
          form,
          this.cdsuraOrigen(),
          this.cdsuraEntrega(),
          this.nmconsecutivoOficina(),
          this.nmconsecutivoCliente(),
          this.nmconsecutivoAsesor(),
          this.onObtenerTarifa()
        ).catch(() => {});
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atención',
          detail: 'Por favor seleccione los municipios de origen y entrega',
          life: 4000
        });
        form.patchValue({
          snpagoTransporteEmpleado: NO_VALUE,
          ptvalorTransporte: null
        });
      }
    } else {
      form.patchValue({ ptvalorTransporte: null });
    }
  }

  private initSubscriptions(form: FormGroup, signals: TransporteSignals): void {
    const controlAdicional = form.get('snrequiereTransporteAdicional');
    const controlValor = form.get('ptvalorTransporteAdicional');
    const controlObservaciones = form.get('dsobservacionesTransporteAdicional');

    const refreshRequiereObservaciones = (): void => {
      const requiereTransporteAdicional = controlAdicional?.value === YES_VALUE;
      const valorTransporteAdicional = Number(controlValor?.value ?? 0);
      signals.requiereObservacionesAdicional.set(
        requiereTransporteAdicional && valorTransporteAdicional > 0
      );
    };

    if (controlAdicional) {
      signals.mostrarTransporteAdicional.set(
        controlAdicional.value === YES_VALUE
      );
      controlAdicional.valueChanges.subscribe((value) => {
        signals.mostrarTransporteAdicional.set(value === YES_VALUE);

        if (value === YES_VALUE) {
          // Regla funcional: al seleccionar "Si" inicia en 0 por defecto.
          controlValor?.setValue(0, { emitEvent: false });
        } else {
          controlValor?.setValue(0, { emitEvent: false });
          controlObservaciones?.setValue('', { emitEvent: false });
        }

        refreshRequiereObservaciones();
      });
    }

    if (controlValor) {
      // El tope (legacy maxTteAdic) se aplica al teclear con appTopeValor y al guardar en
      // CumplimientoFormFacade.validarFormulario.
      controlValor.valueChanges.subscribe((value) => {
        if (value != null && Number(value) < 0) {
          controlValor.setValue(0, { emitEvent: false });
        }
        refreshRequiereObservaciones();
      });
    }

    refreshRequiereObservaciones();
  }

  private cargarMunicipiosIniciales(
    origen: MunicipioPrecargado | null,
    entrega: MunicipioPrecargado | null,
    form: FormGroup,
    signals: TransporteSignals
  ): boolean {
    // Precargamos el form aunque falte dsmunicipio para que el validador pase;
    // marcamos "inicializado" solo cuando llegue dsmunicipio, permitiendo que un
    // refresh async del padre complete el display.
    let origenListo = false;
    let entregaListo = false;
    if (origen?.cdmunicipio) {
      form.patchValue({ cdmunicipioOrigen: origen.cdmunicipio });
      if (origen.dsmunicipio) {
        signals.dsmunicipioOrigen.set(origen.dsmunicipio);
        signals.cdsuraOrigen.set(origen.cdsura || '');
        origenListo = true;
      }
    }
    if (entrega?.cdmunicipio) {
      form.patchValue({ cdmunicipioEntrega: entrega.cdmunicipio });
      if (entrega.dsmunicipio) {
        signals.dsmunicipioEntrega.set(entrega.dsmunicipio);
        signals.cdsuraEntrega.set(entrega.cdsura || '');
        entregaListo = true;
      }
    }
    return origenListo && entregaListo;
  }

  private seleccionarMunicipioOrigen(
    municipio: Municipio,
    form: FormGroup,
    signals: TransporteSignals
  ): void {
    signals.dsmunicipioOrigen.set(municipio.dsmunicipio || '');
    signals.cdsuraOrigen.set(municipio.cdsura || '');
    form.patchValue({ cdmunicipioOrigen: municipio.cdmunicipio });
    this.limpiarPoblacion(form, signals);
    this.recalcularTarifaSiAplica(form);
  }

  private seleccionarMunicipioEntrega(
    municipio: Municipio,
    form: FormGroup,
    signals: TransporteSignals
  ): void {
    signals.dsmunicipioEntrega.set(municipio.dsmunicipio || '');
    signals.cdsuraEntrega.set(municipio.cdsura || '');
    form.patchValue({ cdmunicipioEntrega: municipio.cdmunicipio });
    this.limpiarPoblacion(form, signals);
    this.recalcularTarifaSiAplica(form);
  }

  private seleccionarPoblacion(
    poblacion: Poblacion,
    form: FormGroup,
    signals: TransporteSignals
  ): void {
    const display = poblacion.cdpoblacion
      ? `${poblacion.cdpoblacion} - ${poblacion.dspoblacion}`
      : poblacion.dspoblacion || '';
    signals.dspoblacion.set(display);
    form.patchValue({ cdpoblacion: poblacion.cdpoblacion });
  }

  private limpiarPoblacion(form: FormGroup, signals: TransporteSignals): void {
    signals.dspoblacion.set('');
    form.patchValue({ cdpoblacion: '', ptvalorTransporte: null });
  }

  private resetSignals(signals: TransporteSignals): void {
    signals.dsmunicipioOrigen.set('');
    signals.cdsuraOrigen.set('');
    signals.dsmunicipioEntrega.set('');
    signals.cdsuraEntrega.set('');
    signals.dspoblacion.set('');
  }

  private async cargarTarifaTransporte(
    form: FormGroup,
    cdsuraOrigen: string,
    cdsuraEntrega: string,
    nmconsecutivoOficina: number | undefined,
    nmconsecutivoCliente: number | undefined,
    nmconsecutivoAsesor: number | undefined,
    callback:
      | ((params: {
          cdsuraOrigen: string;
          cdsuraDestino: string;
          cdpoblacion?: string;
          nmconsecutivoOficina?: number;
          nmconsecutivoCliente?: number;
          nmconsecutivoAsesor?: number;
        }) => Promise<number | null>)
      | undefined
  ): Promise<void> {
    if (!cdsuraOrigen || !cdsuraEntrega || !callback) {
      // También invalida cualquier consulta previa en curso.
      this.tarifaRequestId++;
      form.patchValue({ ptvalorTransporte: null });
      return;
    }
    const requestId = ++this.tarifaRequestId;
    const cdpoblacion = form.get('cdpoblacion')?.value || undefined;
    const ptcosto = await callback({
      cdsuraOrigen,
      cdsuraDestino: cdsuraEntrega,
      cdpoblacion,
      nmconsecutivoOficina,
      nmconsecutivoCliente,
      nmconsecutivoAsesor
    });
    // Descarta la respuesta si ya se disparó una petición más reciente
    // (p. ej. el usuario cambió otro campo mientras esta seguía en curso).
    if (requestId !== this.tarifaRequestId) {
      return;
    }
    form.patchValue({ ptvalorTransporte: ptcosto });
  }

  /**
   * Sanitiza el campo Observaciones Transporte Adicional contra XSS/SQLi
   * (seguridad-girop §3).
   */
  sanitizeObservacionesTransporteAdicional(): void {
    const ctrl = this.formGroup().get('dsobservacionesTransporteAdicional');
    if (ctrl) {
      ctrl.setValue(sanitizeTextarea(ctrl.value ?? ''), { emitEvent: false });
    }
  }

  private recalcularTarifaSiAplica(form: FormGroup): void {
    if (form.get('snpagoTransporteEmpleado')?.value !== YES_VALUE) {
      return;
    }
    if (!this.cdsuraOrigen() || !this.cdsuraEntrega()) {
      return;
    }
    this.cargarTarifaTransporte(
      form,
      this.cdsuraOrigen(),
      this.cdsuraEntrega(),
      this.nmconsecutivoOficina(),
      this.nmconsecutivoCliente(),
      this.nmconsecutivoAsesor(),
      this.onObtenerTarifa()
    ).catch(() => {});
  }
}
